import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { fromEvent, merge, Observable, Observer, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { CapturumTemplateDirective, NavBarItem } from '@capturum/ui/api';
import { Element } from '@angular/compiler';

@Component({
  selector: 'cap-horizontal-navbar',
  templateUrl: './horizontal-navbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HorizontalNavbarComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  /**
   * The items to be displayed in the navbar
   */
  @Input()
  public items: NavBarItem[] = [];

  /**
   * the style class to be applied on the navbar
   */
  @Input()
  public styleClass: string = '';

  @ContentChildren(CapturumTemplateDirective)
  public templates: QueryList<CapturumTemplateDirective>;

  public filteredItems: NavBarItem[];
  public lastItemVisible: boolean;
  public firstItemVisible: boolean;
  public navItemTemplate: TemplateRef<any>;
  public navIconTemplate: TemplateRef<any>;

  private destroy$: Subject<void> = new Subject();

  constructor(private cdr: ChangeDetectorRef) {
  }

  public ngOnInit(): void {
    this.isOnline().pipe(takeUntil((this.destroy$))).subscribe((isOnline) => {
      if (this.items) {
        if (!isOnline) {
          this.filteredItems = [...this.items].filter((item) => item.offlineAvailable === true);
        } else {
          this.filteredItems = [...this.items];
        }
      }

      this.cdr.detectChanges();
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public ngAfterViewInit(): void {
    this.templates.forEach((template) => {
      switch (template.getType()) {
        case 'icon':
          this.navIconTemplate = template.template;
          break;
        case 'nav-item':
          this.navItemTemplate = template.template;
          break;
      }
    });

    this.cdr.detectChanges();
  }

  public ngOnChanges(): void {
    // Check whether this.items is iterable
    if (this.items && typeof this.items[Symbol.iterator] === 'function') {
      this.filteredItems = [...this.items];

      if (!navigator.onLine) {
        this.filteredItems = [...this.items].filter((item) => item.offlineAvailable === true);
      }

      this.observe();

      this.cdr.detectChanges();
    }
  }

  private isOnline(): Observable<boolean> {
    return merge<boolean[]>(
      fromEvent(window, 'offline').pipe(map(() => false)),
      fromEvent(window, 'online').pipe(map(() => true)),
      new Observable((sub: Observer<boolean>) => {
        sub.next(navigator.onLine);
      })) as Observable<boolean>;
  }

  private observe(): void {
    const navItemElements = document.querySelectorAll('.nav-item');
    const lastItemObserver = new IntersectionObserver((event: IntersectionObserverEntry[]) => {
      this.lastItemVisible = event[0].isIntersecting;
    });

    const firstItemIntersection = new IntersectionObserver((event: IntersectionObserverEntry[]) => {
      this.firstItemVisible = event[0].isIntersecting;
    });

    if (navItemElements[navItemElements.length - 1] && navItemElements[navItemElements.length - 1] instanceof Element) {
      lastItemObserver.observe(navItemElements[navItemElements.length - 1]);
      firstItemIntersection.observe(navItemElements[0]);

      this.cdr.detectChanges();
    }
  }

}
